import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { api } from '@/shared/lib/api';
import {
  Send,
  X,
  AlertCircle,
  MessageCircle,
  TrendingUp,
  Target,
  HelpCircle,
  Play,
  Calendar,
  Trophy,
  BarChart3,
  ArrowLeft,
  Radio
} from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Badge
} from '@travellegacy/nexops-design-system';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface Message {
  id: string;
  userMessage: string;
  aiClientMessage: string;
  score: number;
  feedbackPresenca: number | null;
  feedbackPriorizacao: number | null;
  feedbackCaptura: number | null;
  feedbackRoteamento: number | null;
  feedbackAcompanhamento: number | null;
  feedbackPortugues: number | null;
  feedbackText: string;
  messageOrder: number;
}

interface Session {
  id: string;
  scenario: string;
  startedAt: string;
  finishedAt: string | null;
  finalScore: number | null;
  totalMessages: number;
}

interface SessionListItem {
  id: string;
  scenario: string;
  startedAt: string;
  finishedAt: string | null;
  finalScore: number | null;
  totalMessages: number;
}

interface Scenario {
  id: string;
  name: string;
  description: string;
  difficulty: 'easy' | 'medium' | 'hard';
}

export function TorreTrainingPage() {
  const location = useLocation();
  const [session, setSession] = useState<Session | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showScenariosModal, setShowScenariosModal] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Buscar cenários Torre disponíveis
  const { data: scenariosData } = useQuery<{ scenarios: Scenario[] }>({
    queryKey: ['torre-scenarios'],
    queryFn: async () => {
      const response = await api.get('/training/torre/scenarios');
      return response.data;
    },
  });

  // Buscar sessões Torre do usuário logado
  const { data: sessionsData, refetch: refetchSessions } = useQuery<{ sessions: SessionListItem[] }>({
    queryKey: ['torre-sessions'],
    queryFn: async () => {
      const response = await api.get('/training/torre/sessions');
      return response.data;
    },
    enabled: !session,
  });

  const sessions = sessionsData?.sessions || [];
  const scenarios = scenariosData?.scenarios || [];

  // Resetar estado quando navegar para a página
  useEffect(() => {
    if (location.pathname === '/training/torre') {
      setSession(null);
      setMessages([]);
      setCurrentMessage('');
      setIsFinished(false);
      setError(null);
    }
  }, [location.pathname]);

  // Scroll para última mensagem
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Iniciar sessão com cenário específico
  const startSession = async (scenarioId: string) => {
    try {
      // Fechar modal imediatamente e mostrar loading
      setShowScenariosModal(false);
      setIsLoading(true);
      setError(null);

      // Chamada com timeout maior (90 segundos) para geração de cenário dinâmico
      const response = await api.post('/training/torre/sessions/start',
        { scenarioId },
        { timeout: 90000 }
      );

      setSession(response.data.session);

      // Adicionar mensagem inicial do "cliente"
      const initialMsg = {
        id: 'initial',
        userMessage: '',
        aiClientMessage: response.data.initialMessage,
        score: 0,
        feedbackPresenca: null,
        feedbackPriorizacao: null,
        feedbackCaptura: null,
        feedbackRoteamento: null,
        feedbackAcompanhamento: null,
        feedbackPortugues: null,
        feedbackText: '',
        messageOrder: 0,
      };
      setMessages([initialMsg]);
    } catch (err) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Erro ao iniciar sessão. Tente novamente.');
      // Reabrir modal em caso de erro para usuário tentar novamente
      setShowScenariosModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Enviar mensagem
  const sendMessage = async () => {
    if (!currentMessage.trim() || !session || isLoading) return;

    const messageText = currentMessage.trim();

    // Optimistic UI: adicionar mensagem do usuário imediatamente
    const tempUserMessage: Message = {
      id: `temp-${Date.now()}`,
      userMessage: messageText,
      aiClientMessage: '',
      score: 0,
      feedbackPresenca: null,
      feedbackPriorizacao: null,
      feedbackCaptura: null,
      feedbackRoteamento: null,
      feedbackAcompanhamento: null,
      feedbackPortugues: null,
      feedbackText: '',
      messageOrder: messages.length,
    };

    setMessages((prev) => [...prev, tempUserMessage]);
    setCurrentMessage('');

    try {
      setIsLoading(true);
      setError(null);

      const response = await api.post(`/training/torre/sessions/${session.id}/message`, {
        message: messageText,
      });

      // Substituir mensagem temporária pela resposta real do servidor
      setMessages((prev) => {
        const withoutTemp = prev.filter(m => m.id !== tempUserMessage.id);
        return [...withoutTemp, response.data.message];
      });

      if (response.data.sessionFinished) {
        setIsFinished(true);
      }
    } catch (err) {
      const error = err as { response?: { data?: { error?: string } } };
      // Remover mensagem temporária em caso de erro
      setMessages((prev) => prev.filter(m => m.id !== tempUserMessage.id));
      setError(error.response?.data?.error || 'Erro ao enviar mensagem');
    } finally {
      setIsLoading(false);
    }
  };

  // Encerrar sessão manualmente
  const finishSession = async () => {
    if (!session || isLoading) return;

    try {
      setIsLoading(true);
      await api.post(`/training/torre/sessions/${session.id}/finish`, {});
      setIsFinished(true);
    } catch (err) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Erro ao encerrar sessão');
    } finally {
      setIsLoading(false);
    }
  };

  // Carregar sessão existente
  const loadSession = async (sessionId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get(`/training/torre/sessions/${sessionId}`);
      const { session: sessionData, messages: messagesData } = response.data;

      setSession(sessionData);
      setMessages(messagesData);
      setIsFinished(sessionData.finishedAt !== null);
    } catch (err) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Erro ao carregar sessão');
    } finally {
      setIsLoading(false);
    }
  };

  // Reiniciar
  const restart = () => {
    setSession(null);
    setMessages([]);
    setCurrentMessage('');
    setIsFinished(false);
    setError(null);
    refetchSessions();
  };

  // Formatar data de forma segura
  const formatSafeDate = (date: string | Date) => {
    try {
      const parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) {
        return 'Data inválida';
      }
      return format(parsedDate, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return 'Data inválida';
    }
  };

  // Calcular melhor score de forma segura
  const getBestScore = (sessions: SessionListItem[]) => {
    if (sessions.length === 0) return 0;
    const scores = sessions.map(s => s.finalScore || 0).filter(score => score > 0);
    return scores.length > 0 ? Math.max(...scores) : 0;
  };

  // Renderizar nota com cor
  const getScoreColor = (score: number) => {
    if (score >= 8) return 'text-green-600 dark:text-green-400';
    if (score >= 6) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 8) return 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800';
    if (score >= 6) return 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800';
    return 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800';
  };

  const getScoreBadgeBg = (score: number) => {
    if (score >= 8) return 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700';
    if (score >= 6) return 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-700';
    return 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700';
  };

  const getScoreBadgeVariant = (score: number): 'success' | 'warning' | 'error' => {
    if (score >= 8) return 'success';
    if (score >= 6) return 'warning';
    return 'error';
  };

  const getDifficultyBadge = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return <Badge variant="success">Fácil</Badge>;
      case 'medium':
        return <Badge variant="warning">Médio</Badge>;
      case 'hard':
        return <Badge variant="error">Difícil</Badge>;
      default:
        return <Badge>{difficulty}</Badge>;
    }
  };

  // Modal "Como funciona"
  const HelpModal = () => {
    if (!showHelpModal) return null;

    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowHelpModal(false)}>
        <Card className="w-full max-w-2xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Radio className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <CardTitle>Como funciona o Treinamento da Torre?</CardTitle>
                  <CardDescription>Aprenda praticando a função Torre em situações reais</CardDescription>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowHelpModal(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3 text-sm">
              <div className="flex gap-3">
                <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xs">
                  1
                </div>
                <div>
                  <p className="font-medium text-foreground">Escolha um cenário específico da Torre</p>
                  <p className="text-muted-foreground">Situações reais: múltiplas solicitações, urgências, captura de dados, follow-up</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xs">
                  2
                </div>
                <div>
                  <p className="font-medium text-foreground">Cliente simulado com contexto Travel Legacy</p>
                  <p className="text-muted-foreground">IA simula clientes reais com demandas típicas da Torre</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xs">
                  3
                </div>
                <div>
                  <p className="font-medium text-foreground">Você responde como Torre</p>
                  <p className="text-muted-foreground">Dê presença, priorize, capture dados, roteie e acompanhe</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xs">
                  4
                </div>
                <div>
                  <p className="font-medium text-foreground">IA avalia sua atuação na Torre</p>
                  <p className="text-muted-foreground">Feedback em tempo real com nota de 0 a 10 por critério Torre</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xs">
                  5
                </div>
                <div>
                  <p className="font-medium text-foreground">Resumo final com pontos de melhoria</p>
                  <p className="text-muted-foreground">Análise detalhada específica para função Torre</p>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-2">Critérios de Avaliação da Torre</h4>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="font-medium text-foreground">Presença</p>
                  <p className="text-muted-foreground">Respondeu rapidamente (até 15 min)?</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="font-medium text-foreground">Priorização</p>
                  <p className="text-muted-foreground">Identificou urgências corretamente?</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="font-medium text-foreground">Captura</p>
                  <p className="text-muted-foreground">Coletou todos os dados necessários?</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="font-medium text-foreground">Roteamento</p>
                  <p className="text-muted-foreground">Encaminhou para especialista correto?</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="font-medium text-foreground">Acompanhamento</p>
                  <p className="text-muted-foreground">Manteve cliente informado sobre andamento?</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="font-medium text-foreground">Português</p>
                  <p className="text-muted-foreground">Gramática, ortografia e didática corretas?</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Modal de seleção de cenários
  const ScenariosModal = () => {
    if (!showScenariosModal) return null;

    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowScenariosModal(false)}>
        <Card className="w-full max-w-3xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Escolha um Cenário</CardTitle>
                <CardDescription>Selecione um cenário para iniciar seu treinamento da Torre</CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowScenariosModal(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {scenarios.map((scenario) => (
                <Card
                  key={scenario.id}
                  className={`cursor-pointer hover:border-primary transition-colors ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}
                  onClick={() => !isLoading && startSession(scenario.id)}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-lg">{scenario.name}</h3>
                      {getDifficultyBadge(scenario.difficulty)}
                    </div>
                    <p className="text-sm text-muted-foreground">{scenario.description}</p>
                    <p className="text-xs text-primary mt-2">✨ Cada simulação é única e gerada por IA</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Tela inicial - Lista de sessões
  if (!session) {
    const completedSessions = sessions.filter(s => s.finishedAt && s.finalScore !== null);
    const avgScore = completedSessions.length > 0
      ? completedSessions.reduce((sum, s) => sum + (s.finalScore || 0), 0) / completedSessions.length
      : 0;

    // Loading enquanto gera cenário
    if (isLoading) {
      return (
        <div className="min-h-[60vh] flex items-center justify-center">
          <Card className="w-full max-w-md">
            <CardContent className="pt-8 pb-8">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto animate-pulse">
                  <Play className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">Gerando Cenário Único</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Nossa IA está criando uma simulação exclusiva para você...
                  </p>
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Isso pode levar alguns segundos...</p>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Radio className="h-8 w-8" />
              Treinamento da Torre
            </h1>
            <p className="text-muted-foreground mt-2">
              Pratique suas habilidades como Torre em cenários realistas baseados no Manual da Torre Ugoo
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="outline" onClick={() => setShowHelpModal(true)}>
              <HelpCircle className="h-4 w-4 mr-2" />
              Como funciona
            </Button>
            <Button onClick={() => setShowScenariosModal(true)} disabled={isLoading}>
              <Play className="h-4 w-4 mr-2" />
              {isLoading ? 'Gerando cenário único...' : 'Nova Simulação'}
            </Button>
          </div>
        </div>

        {/* Estatísticas gerais */}
        {completedSessions.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Sessões Completas</p>
                    <p className="text-2xl font-bold">{completedSessions.length}</p>
                  </div>
                  <Trophy className="h-8 w-8 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Média Geral</p>
                    <p className={`text-2xl font-bold ${getScoreColor(avgScore)}`}>
                      {avgScore.toFixed(1)}
                    </p>
                  </div>
                  <BarChart3 className="h-8 w-8 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Mensagens</p>
                    <p className="text-2xl font-bold">
                      {completedSessions.reduce((sum, s) => sum + s.totalMessages, 0)}
                    </p>
                  </div>
                  <MessageCircle className="h-8 w-8 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Melhor Score</p>
                    <p className={`text-2xl font-bold ${getScoreColor(getBestScore(completedSessions))}`}>
                      {getBestScore(completedSessions).toFixed(1)}
                    </p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabela de histórico */}
        <Card>
          <CardHeader>
            <CardTitle>Histórico de Treinamentos Torre</CardTitle>
            <CardDescription>
              {sessions.length === 0 ? 'Nenhum treinamento realizado ainda' : `${sessions.length} sessão(ões) registrada(s)`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sessions.length === 0 ? (
              <div className="text-center py-12">
                <Radio className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">
                  Comece sua jornada como Torre! Inicie sua primeira simulação para praticar suas habilidades.
                </p>
                <Button onClick={() => setShowScenariosModal(true)} disabled={isLoading}>
                  <Play className="h-4 w-4 mr-2" />
                  Iniciar Primeira Simulação
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Data</th>
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Cenário</th>
                      <th className="text-center p-3 text-sm font-medium text-muted-foreground">Mensagens</th>
                      <th className="text-center p-3 text-sm font-medium text-muted-foreground">Score Final</th>
                      <th className="text-center p-3 text-sm font-medium text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((session) => (
                      <tr
                        key={session.id}
                        className="border-b hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => loadSession(session.id)}
                      >
                        <td className="p-3">
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            {formatSafeDate(session.startedAt)}
                          </div>
                        </td>
                        <td className="p-3">
                          <p className="text-sm font-medium">{session.scenario}</p>
                        </td>
                        <td className="p-3 text-center">
                          <Badge>{session.totalMessages}</Badge>
                        </td>
                        <td className="p-3 text-center">
                          {session.finalScore !== null && session.finalScore !== undefined ? (
                            <span className={`text-lg font-bold ${getScoreColor(session.finalScore)}`}>
                              {session.finalScore.toFixed(1)}
                            </span>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          {session.finishedAt ? (
                            <Badge variant={session.finalScore && session.finalScore >= 7 ? 'success' : 'warning'}>
                              Concluída
                            </Badge>
                          ) : (
                            <Badge>Em andamento</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {error && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
          </div>
        )}

        <HelpModal />
        <ScenariosModal />
      </div>
    );
  }

  // Resumo final
  if (isFinished && messages.length > 1) {
    const validMessages = messages.filter((m) => m.messageOrder > 0);
    const avgScore =
      validMessages.reduce((sum, m) => sum + m.score, 0) / validMessages.length;
    const avgPresenca =
      validMessages.reduce((sum, m) => sum + (m.feedbackPresenca || 0), 0) /
      validMessages.length;
    const avgPriorizacao =
      validMessages.reduce((sum, m) => sum + (m.feedbackPriorizacao || 0), 0) /
      validMessages.length;
    const avgCaptura =
      validMessages.reduce((sum, m) => sum + (m.feedbackCaptura || 0), 0) / validMessages.length;
    const avgRoteamento =
      validMessages.reduce((sum, m) => sum + (m.feedbackRoteamento || 0), 0) /
      validMessages.length;
    const avgAcompanhamento =
      validMessages.reduce((sum, m) => sum + (m.feedbackAcompanhamento || 0), 0) /
      validMessages.length;
    const avgPortugues =
      validMessages.reduce((sum, m) => sum + (m.feedbackPortugues || 0), 0) /
      validMessages.length;

    return (
      <div className="space-y-6">
        {/* Botão voltar */}
        <Button variant="ghost" size="sm" onClick={restart} className="mb-2">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar ao Histórico
        </Button>

        {/* Header do resumo */}
        <Card className="bg-gradient-to-br from-primary/10 via-primary/5 to-background border-2 border-primary/20">
          <CardContent className="pt-8 pb-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trophy className="w-10 h-10 text-primary" />
              </div>
              <h2 className="text-3xl font-bold mb-2">Treinamento Torre Concluído!</h2>
              <p className="text-muted-foreground mb-6">
                Você completou a simulação. Veja seu desempenho como Torre abaixo.
              </p>

              <div className="flex items-center justify-center gap-3">
                <span className="text-sm text-muted-foreground">Pontuação Final:</span>
                <div className={`px-6 py-3 rounded-full text-4xl font-bold ${getScoreBadgeBg(avgScore)}`}>
                  {avgScore.toFixed(1)}
                  <span className="text-lg ml-1">/10</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Métricas detalhadas */}
        <Card>
          <CardHeader>
            <CardTitle>Detalhamento por Critério Torre</CardTitle>
            <CardDescription>Análise das 6 dimensões avaliadas (5 critérios Torre + Português)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-lg border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Presença</span>
                  <span className={`text-xl font-bold ${getScoreColor(avgPresenca)}`}>
                    {avgPresenca.toFixed(1)}
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      avgPresenca >= 8
                        ? 'bg-green-500'
                        : avgPresenca >= 6
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                    }`}
                    style={{ width: `${(avgPresenca / 10) * 100}%` }}
                  />
                </div>
              </div>

              <div className="p-4 rounded-lg border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Priorização</span>
                  <span className={`text-xl font-bold ${getScoreColor(avgPriorizacao)}`}>
                    {avgPriorizacao.toFixed(1)}
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      avgPriorizacao >= 8
                        ? 'bg-green-500'
                        : avgPriorizacao >= 6
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                    }`}
                    style={{ width: `${(avgPriorizacao / 10) * 100}%` }}
                  />
                </div>
              </div>

              <div className="p-4 rounded-lg border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Captura</span>
                  <span className={`text-xl font-bold ${getScoreColor(avgCaptura)}`}>
                    {avgCaptura.toFixed(1)}
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      avgCaptura >= 8 ? 'bg-green-500' : avgCaptura >= 6 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${(avgCaptura / 10) * 100}%` }}
                  />
                </div>
              </div>

              <div className="p-4 rounded-lg border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Roteamento</span>
                  <span className={`text-xl font-bold ${getScoreColor(avgRoteamento)}`}>
                    {avgRoteamento.toFixed(1)}
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      avgRoteamento >= 8
                        ? 'bg-green-500'
                        : avgRoteamento >= 6
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                    }`}
                    style={{ width: `${(avgRoteamento / 10) * 100}%` }}
                  />
                </div>
              </div>

              <div className="p-4 rounded-lg border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Acompanhamento</span>
                  <span className={`text-xl font-bold ${getScoreColor(avgAcompanhamento)}`}>
                    {avgAcompanhamento.toFixed(1)}
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      avgAcompanhamento >= 8
                        ? 'bg-green-500'
                        : avgAcompanhamento >= 6
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                    }`}
                    style={{ width: `${(avgAcompanhamento / 10) * 100}%` }}
                  />
                </div>
              </div>

              <div className="p-4 rounded-lg border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Português</span>
                  <span className={`text-xl font-bold ${getScoreColor(avgPortugues)}`}>
                    {avgPortugues.toFixed(1)}
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      avgPortugues >= 8
                        ? 'bg-green-500'
                        : avgPortugues >= 6
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                    }`}
                    style={{ width: `${(avgPortugues / 10) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Histórico da conversa */}
        <Card>
          <CardHeader>
            <CardTitle>Histórico da Conversa</CardTitle>
            <CardDescription>Revise suas respostas e feedbacks recebidos</CardDescription>
          </CardHeader>
          <CardContent className="max-h-96 overflow-y-auto">
            <div className="space-y-4">
              {messages.map((msg) => (
                <div key={msg.id}>
                  {/* Mensagem do cliente */}
                  {msg.aiClientMessage && (
                    <div className="flex justify-start mb-3">
                      <Card className="max-w-[80%] shadow-sm">
                        <CardContent className="pt-4">
                          <p className="text-sm leading-relaxed">{msg.aiClientMessage}</p>
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {/* Mensagem do usuário */}
                  {msg.userMessage && (
                    <div className="flex justify-end mb-3">
                      <Card className="max-w-[80%] bg-primary text-primary-foreground shadow-sm">
                        <CardContent className="pt-4">
                          <p className="text-sm leading-relaxed">{msg.userMessage}</p>
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {/* Feedback */}
                  {msg.messageOrder > 0 && msg.feedbackText && (
                    <div className="mb-6">
                      <Card className={`${getScoreBgColor(msg.score)} shadow-md border-2`}>
                        <CardContent className="pt-5 pb-5">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary/70 rounded-lg flex items-center justify-center">
                                <Target className="w-4 h-4 text-white" />
                              </div>
                              <span className="text-xs font-medium text-muted-foreground">
                                Resposta {msg.messageOrder}
                              </span>
                            </div>
                            <Badge variant={getScoreBadgeVariant(msg.score)}>
                              {msg.score.toFixed(1)} / 10
                            </Badge>
                          </div>
                          <p className="text-sm text-foreground leading-relaxed mb-3">
                            {msg.feedbackText}
                          </p>

                          {/* Métricas individuais Torre */}
                          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 pt-3 border-t">
                            <div className="text-center">
                              <p className="text-xs text-muted-foreground mb-1">Presença</p>
                              <p className={`text-sm font-bold ${getScoreColor(msg.feedbackPresenca || 0)}`}>
                                {msg.feedbackPresenca?.toFixed(1) || '-'}
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-muted-foreground mb-1">Priorização</p>
                              <p className={`text-sm font-bold ${getScoreColor(msg.feedbackPriorizacao || 0)}`}>
                                {msg.feedbackPriorizacao?.toFixed(1) || '-'}
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-muted-foreground mb-1">Captura</p>
                              <p className={`text-sm font-bold ${getScoreColor(msg.feedbackCaptura || 0)}`}>
                                {msg.feedbackCaptura?.toFixed(1) || '-'}
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-muted-foreground mb-1">Roteamento</p>
                              <p className={`text-sm font-bold ${getScoreColor(msg.feedbackRoteamento || 0)}`}>
                                {msg.feedbackRoteamento?.toFixed(1) || '-'}
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-muted-foreground mb-1">Acompanh.</p>
                              <p className={`text-sm font-bold ${getScoreColor(msg.feedbackAcompanhamento || 0)}`}>
                                {msg.feedbackAcompanhamento?.toFixed(1) || '-'}
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-muted-foreground mb-1">Português</p>
                              <p className={`text-sm font-bold ${getScoreColor(msg.feedbackPortugues || 0)}`}>
                                {msg.feedbackPortugues?.toFixed(1) || '-'}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Ações finais */}
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={restart}>
            Ver Histórico
          </Button>
          <Button onClick={() => setShowScenariosModal(true)} disabled={isLoading}>
            <Play className="h-4 w-4 mr-2" />
            Nova Simulação
          </Button>
        </div>
      </div>
    );
  }

  // Chat ativo
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={restart}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h2 className="text-2xl font-bold">Simulação Torre em Andamento</h2>
            <p className="text-sm text-muted-foreground">Cenário: {session.scenario}</p>
          </div>
        </div>
        {!isFinished && (
          <Button variant="outline" onClick={finishSession} disabled={isLoading}>
            <X className="h-4 w-4 mr-2" />
            Encerrar
          </Button>
        )}
      </div>

      {/* Chat */}
      <Card>
        <CardContent className="p-6">
          <div className="h-[500px] overflow-y-auto mb-4 space-y-4">
            {messages.map((msg) => (
              <div key={msg.id}>
                {/* Mensagem do cliente (inicial) */}
                {msg.messageOrder === 0 && msg.aiClientMessage && (
                  <div className="flex justify-start mb-3">
                    <Card className="max-w-[70%] shadow-sm">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-2">
                          <MessageCircle className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs font-medium text-muted-foreground">Cliente</span>
                        </div>
                        <p className="text-sm leading-relaxed">{msg.aiClientMessage}</p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Mensagem do usuário */}
                {msg.userMessage && (
                  <div className="flex justify-end mb-3">
                    <Card className="max-w-[70%] bg-primary text-primary-foreground shadow-sm">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-medium opacity-90">Você (Torre)</span>
                        </div>
                        <p className="text-sm leading-relaxed">{msg.userMessage}</p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Card de feedback */}
                {msg.messageOrder > 0 && msg.feedbackText && (
                  <Card className={`${getScoreBgColor(msg.score)} shadow-md border-2 mb-6`}>
                    <CardContent className="pt-5 pb-5">
                      {/* Header com ícone e score */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary/70 rounded-lg flex items-center justify-center">
                            <Target className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold">Avaliação da IA</p>
                            <p className="text-xs text-muted-foreground">Análise da sua resposta como Torre</p>
                          </div>
                        </div>
                        <div className={`px-4 py-2 rounded-full ${getScoreBadgeBg(msg.score)}`}>
                          <span className="text-2xl font-bold">{msg.score.toFixed(1)}</span>
                          <span className="text-sm font-medium ml-1">/10</span>
                        </div>
                      </div>

                      {/* Feedback text */}
                      <p className="text-sm text-foreground leading-relaxed mb-4">
                        {msg.feedbackText}
                      </p>

                      {/* Métricas individuais Torre */}
                      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 pt-3 border-t">
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground mb-1">Presença</p>
                          <p className={`text-sm font-bold ${getScoreColor(msg.feedbackPresenca || 0)}`}>
                            {msg.feedbackPresenca?.toFixed(1) || '-'}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground mb-1">Priorização</p>
                          <p className={`text-sm font-bold ${getScoreColor(msg.feedbackPriorizacao || 0)}`}>
                            {msg.feedbackPriorizacao?.toFixed(1) || '-'}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground mb-1">Captura</p>
                          <p className={`text-sm font-bold ${getScoreColor(msg.feedbackCaptura || 0)}`}>
                            {msg.feedbackCaptura?.toFixed(1) || '-'}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground mb-1">Roteamento</p>
                          <p className={`text-sm font-bold ${getScoreColor(msg.feedbackRoteamento || 0)}`}>
                            {msg.feedbackRoteamento?.toFixed(1) || '-'}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground mb-1">Acompanh.</p>
                          <p className={`text-sm font-bold ${getScoreColor(msg.feedbackAcompanhamento || 0)}`}>
                            {msg.feedbackAcompanhamento?.toFixed(1) || '-'}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground mb-1">Português</p>
                          <p className={`text-sm font-bold ${getScoreColor(msg.feedbackPortugues || 0)}`}>
                            {msg.feedbackPortugues?.toFixed(1) || '-'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Próxima mensagem do cliente (aparece depois do feedback) */}
                {msg.messageOrder > 0 && msg.aiClientMessage && (
                  <div className="flex justify-start mb-3">
                    <Card className="max-w-[70%] shadow-sm">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-2">
                          <MessageCircle className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs font-medium text-muted-foreground">Cliente</span>
                        </div>
                        <p className="text-sm leading-relaxed">{msg.aiClientMessage}</p>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            ))}

            {/* Typing indicator */}
            {isLoading && (
              <div className="flex justify-start mb-3">
                <Card className="max-w-[70%] shadow-sm">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-3">
                      <MessageCircle className="h-4 w-4 text-muted-foreground" />
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                          <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                        </div>
                        <span className="text-xs text-muted-foreground">IA está pensando...</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="flex gap-2 mt-4">
            <input
              type="text"
              value={currentMessage}
              onChange={(e) => setCurrentMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
              placeholder="Digite sua resposta como Torre..."
              className="flex-1 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
              disabled={isLoading}
            />
            <Button onClick={sendMessage} disabled={isLoading || !currentMessage.trim()}>
              <Send className="h-4 w-4" />
            </Button>
          </div>

          {error && (
            <div className="mt-3 p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
